/**
 * Per-client "Domains" tab. Admin sees it inside the client workspace
 * (/clients/:clientId/domains) with domain names linking to a detail page;
 * client-portal users see the same page at /portal/domains with links to
 * the read-only portal detail page.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, Loader2, Globe2 } from "lucide-react";
import { expiryBadge } from "@/components/domains/ZoneRecordsEditor";

export default function ClientDomainsPage() {
  const params = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const clientId = params.clientId || user?.client_id;

  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}/domains`);
      setDomains(data?.domains || []);
    } catch { setDomains([]); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const detailBase = isAdmin ? `/clients/${clientId}/domains` : `/portal/domains`;

  return (
    <div className="space-y-4" data-testid="client-domains-page">
      <div className="flex items-center gap-2.5">
        <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#002FA7]/10">
          <Globe2 className="h-4 w-4 text-[#002FA7]" />
        </span>
        <div>
          <h2 className="font-display font-bold text-xl">Domain Names</h2>
          <p className="text-[12px] text-zinc-500">
            {isAdmin
              ? "Domains assigned to this client. Click a domain to view full details and DNS records."
              : "The domain names we manage for you. Click a domain to view expiry and DNS records."}
          </p>
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading domains…
        </CardContent></Card>
      ) : domains.length === 0 ? (
        <Card className="rounded-sm border-dashed"><CardContent className="p-10 text-center text-sm text-zinc-500"
          data-testid="client-domains-empty">
          <Globe2 className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
          No domains assigned {isAdmin ? "to this client yet — assign them from the Domain Names page." : "yet."}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {domains.map((d) => {
            const badge = expiryBadge(d.expiry_date);
            return (
              <Link key={d.domain_id} to={`${detailBase}/${d.domain_id}`}
                data-testid={`client-domain-${d.domain_id}`}
                className="block">
                <Card className="rounded-sm hover:border-[#002FA7]/40 hover:bg-[#002FA7]/[0.02] transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-mono font-bold text-sm truncate text-[#002FA7]">{d.domain}</span>
                          {d.business_unit_id && (
                            <Badge variant="outline" className={`rounded-sm text-[9px] ${
                              d.is_primary ? "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                              {d.is_primary ? "primary" : "redirect"}
                            </Badge>
                          )}
                          {d.renewal_cost != null && (
                            <span className="text-[10px] font-mono text-zinc-500">
                              ${Number(d.renewal_cost).toFixed(2)}
                              {d.renewal_cost_frequency === "monthly" ? "/mo"
                                : d.renewal_cost_frequency === "quarterly" ? "/qtr"
                                : "/yr"}
                            </span>
                          )}
                          <Badge variant="outline" className={`rounded-sm text-[9px] ${badge.cls}`}>
                            {d.expiry_date ? `expires ${d.expiry_date} · ${badge.label}` : "expiry unknown"}
                          </Badge>
                          {d.autorenew === true ? (
                            <Badge variant="outline"
                              className="rounded-sm text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"
                              data-testid={`client-domain-autorenew-on-${d.domain_id}`}>
                              auto-renew on
                            </Badge>
                          ) : d.autorenew === false ? (
                            <Badge variant="outline"
                              className="rounded-sm text-[9px] bg-amber-50 text-amber-800 border-amber-200"
                              data-testid={`client-domain-autorenew-off-${d.domain_id}`}>
                              auto-renew off
                            </Badge>
                          ) : null}
                          <span className="text-[10px] font-mono text-zinc-400 ml-auto">TPP Wholesale</span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-zinc-300 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
